import React from 'react';
import cn from 'classnames';
import flatten from 'flat';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import get from 'lodash.get';
import styles from './form.module.scss';
import Label from './Label';
import Page from './Page';
import Row from './Row';
import Section from './Section';
import { animateScroll as scroll } from 'react-scroll';

const ERROR_SCROLL_OFFSET = 200;

const Form = ({
  id,
  onSubmit,
  onError,
  defaultValues,
  children,
  disableAutocomplete,
  mode,
  reValidateMode,
  ...props
}) => {
  const { handleSubmit, ...formMethods } = useForm({
    defaultValues,
    shouldFocusError: false,
    mode: mode || undefined,
    reValidateMode: reValidateMode || undefined,
  });

  const scrollToErrors = (errors) => {
    // errors match the field structure, which may include nested values like:
    // arrayField: [
    //   value1: {
    //     ref: <element />,
    //   }
    //   value2: {
    //     ref: <element />,
    //   }
    // ]
    // We don't care about that structure when scrolling to the field, just that there's an error somewhere that we might need to scroll to

    // limit the depth of the search to a reasonable level
    const fieldNames = flatten(formMethods.getValues(), { maxDepth: 5 });

    const offsets = Object.keys(fieldNames)
      .map((name) =>
        // we only want refs from the errors, if the error doesn't have a field we can't scroll to it
        // refs here are the form controls managed by react-hook-form
        // errors without a field should generally trigger an error in another field that controls their value though
        [
          name,
          get(errors, name)?.ref?.parentElement?.getBoundingClientRect().top +
            window.pageYOffset -
            ERROR_SCROLL_OFFSET,
        ]
      )
      .filter((o) => !!o[1]);

    if (!offsets.length) return;

    offsets.sort((a, b) => a[1] - b[1]);
    scroll.scrollTo(offsets[0][1], {
      smooth: true,
    });
    formMethods.setFocus(offsets[0][0]);
  };

  const handleSubmitError = (errors) => {
    scrollToErrors(errors);
    onError();
  };

  const triggerSubmit = handleSubmit(onSubmit, handleSubmitError);

  return (
    <FormProvider
      {...formMethods}
      triggerSubmit={triggerSubmit}
      defaultValues={defaultValues}
    >
      <form
        id={id}
        // eslint-disable-next-line react/prop-types
        className={cn(styles.form, props.className)}
        onSubmit={triggerSubmit}
        autoComplete={disableAutocomplete ? 'off' : undefined}
      >
        {children}
      </form>
    </FormProvider>
  );
};

Form.propTypes = {
  /** The ID of the form element. Useful to select the form during testing. */
  id: PropTypes.string,
  /** Callback function to run after form submission */
  onSubmit: PropTypes.func,
  /** Callback function to run when there is a validation error after submission */
  onError: PropTypes.func,
  /** Default values for form fields */
  defaultValues: PropTypes.object,
  /** URL the form should post to */
  action: PropTypes.string,
  /** Child elements in the form. Inputs are automatically provided with a context to store values in the form */
  children: PropTypes.any,
  /** Sets autocomplete="off" on the component */
  disableAutocomplete: PropTypes.bool,
  /** https://react-hook-form.com/api/useform#props mode */
  mode: PropTypes.string,
  /** https://react-hook-form.com/api/useform#props reValidateMode */
  reValidateMode: PropTypes.string,
};

Form.defaultProps = {
  onSubmit: () => {},
  onError: () => {},
  defaultValues: undefined,
  action: undefined,
  children: undefined,
  disableAutocomplete: false,
  mode: undefined,
  reValidateMode: undefined,
};

Form.Label = Label;
Form.Page = Page;
Form.Row = Row;
Form.Section = Section;
export default Form;
